import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { basename, dirname, ensureMdExtension, extname, joinPath } from '@rbv/shared';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { vaultFileUrl } from '../navigation.js';
import { exportNoteToPdf } from '../features/PrintNote.jsx';
import { loadStringSet, saveStringSet, useVaultFiles } from './hooks.js';
import {
  allFolderPaths,
  buildTree,
  untitledFolder,
  untitledPath,
  type TreeFile,
  type TreeFolder,
} from './treeModel.js';

const EXPANDED_KEY = 'rbv.tree.expanded';

const FILE_ICON: Record<TreeFile['kind'], string> = {
  note: '📄',
  canvas: '🗺',
  base: '🗃',
  file: '📎',
};

type MenuState =
  | { type: 'file'; file: TreeFile; x: number; y: number }
  | { type: 'folder'; folder: TreeFolder; x: number; y: number };

type RenameState = { type: 'file'; id: string } | { type: 'folder'; path: string };

type MoveState = { type: 'file'; id: string; path: string } | { type: 'folder'; path: string };

interface TreeCtx {
  expanded: Set<string>;
  activeId: string | null;
  renaming: RenameState | null;
  toggleFolder(path: string): void;
  openFile(f: TreeFile, newTab?: boolean): void;
  openFileMenu(f: TreeFile, x: number, y: number): void;
  openFolderMenu(f: TreeFolder, x: number, y: number): void;
  finishRename(value: string): void;
  cancelRename(): void;
}

export function FolderTree() {
  const files = useVaultFiles();
  const view = useApp((s) => s.view);
  const openNote = useApp((s) => s.openNote);
  const openView = useApp((s) => s.openView);

  const [expanded, setExpanded] = useState<Set<string>>(() => loadStringSet(EXPANDED_KEY));
  const [ephemeral, setEphemeral] = useState<Set<string>>(() => new Set());
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [renaming, setRenaming] = useState<RenameState | null>(null);
  const [moving, setMoving] = useState<MoveState | null>(null);

  // Ephemeral empty folders live only until a real file lands in (or under) them.
  useEffect(() => {
    setEphemeral((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const p of prev) {
        for (const [, e] of files) {
          if (!e.deleted && (dirname(e.path) === p || dirname(e.path).startsWith(`${p}/`))) {
            next.delete(p);
            changed = true;
            break;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [files]);

  const tree = useMemo(() => buildTree(files.entries(), ephemeral), [files, ephemeral]);

  const activeId =
    view.kind === 'note' || view.kind === 'canvas' || (view.kind === 'base' && view.id)
      ? (view.id ?? null)
      : null;

  const setExpandedPersist = useCallback((next: Set<string>) => {
    setExpanded(next);
    saveStringSet(EXPANDED_KEY, next);
  }, []);

  const toggleFolder = useCallback(
    (path: string) => {
      const next = new Set(expanded);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      setExpandedPersist(next);
    },
    [expanded, setExpandedPersist],
  );

  const expandTo = useCallback(
    (path: string) => {
      const next = new Set(expanded);
      for (let p = path; p; p = dirname(p)) next.add(p);
      setExpandedPersist(next);
    },
    [expanded, setExpandedPersist],
  );

  const openFile = useCallback(
    (f: TreeFile, newTab = false) => {
      const opts = newTab ? { newTab: true } : undefined;
      if (f.kind === 'note') openNote(f.id, opts);
      else if (f.kind === 'canvas') openView({ kind: 'canvas', id: f.id }, opts);
      else if (f.kind === 'base') openView({ kind: 'base', id: f.id }, opts);
      else window.open(vaultFileUrl(f.path), '_blank', 'noopener');
    },
    [openNote, openView],
  );

  // ---------------------------------------------------------------- create

  const newNote = (dir = '') => {
    const p = untitledPath(files.entries(), dir, 'Untitled', '.md');
    if (dir) expandTo(dir);
    void vault.createNote(p).then((id) => openNote(id));
  };

  const newCanvas = () => {
    const p = untitledPath(files.entries(), '', 'Untitled', '.canvas');
    void vault.createNote(p).then((id) => openView({ kind: 'canvas', id }));
  };

  const newFolder = (dir = '') => {
    const p = untitledFolder(allFolderPaths(tree), dir);
    setEphemeral((prev) => new Set(prev).add(p));
    if (dir) expandTo(dir);
    setRenaming({ type: 'folder', path: p });
  };

  const collapseAll = () => setExpandedPersist(new Set());

  // ------------------------------------------------------------ move/rename

  /** Move/rename a folder: vault rename for real files + remap UI state. */
  const moveFolderTo = useCallback(
    (oldPath: string, newPath: string) => {
      let hasFiles = false;
      for (const [, e] of files) {
        if (!e.deleted && e.path.startsWith(`${oldPath}/`)) {
          hasFiles = true;
          break;
        }
      }
      if (hasFiles) vault.renameFolder(oldPath, newPath);
      const remap = (p: string) =>
        p === oldPath ? newPath : p.startsWith(`${oldPath}/`) ? newPath + p.slice(oldPath.length) : p;
      setEphemeral((prev) => new Set([...prev].map(remap)));
      setExpandedPersist(new Set([...expanded].map(remap)));
    },
    [files, expanded, setExpandedPersist],
  );

  const finishRename = useCallback(
    (value: string) => {
      const r = renaming;
      setRenaming(null);
      if (!r) return;
      const name = value.trim().replace(/[/\\]/g, '');
      if (!name) return;
      if (r.type === 'file') {
        const entry = vault.getEntry(r.id);
        if (!entry) return;
        let fileName = name;
        if (entry.kind === 'note') fileName = ensureMdExtension(name);
        else if (entry.kind === 'canvas')
          fileName = name.toLowerCase().endsWith('.canvas') ? name : `${name}.canvas`;
        else if (!extname(name)) fileName = `${name}${extname(entry.path)}`;
        const newPath = joinPath(dirname(entry.path), fileName);
        if (newPath === entry.path) return;
        const clash = vault.findByPath(newPath);
        if (clash && clash !== r.id) {
          alert(`"${newPath}" already exists.`);
          return;
        }
        vault.renameFile(r.id, newPath);
      } else {
        const newPath = joinPath(dirname(r.path), name);
        if (newPath !== r.path) moveFolderTo(r.path, newPath);
      }
    },
    [renaming, moveFolderTo],
  );

  const cancelRename = useCallback(() => setRenaming(null), []);

  // ---------------------------------------------------------------- delete

  const deleteFileRow = (f: TreeFile) => {
    if (!confirm(`Delete "${f.name}"?`)) return;
    vault.deleteFile(f.id);
  };

  const deleteFolderRow = (folder: TreeFolder) => {
    const ids: string[] = [];
    for (const [id, e] of files) {
      if (!e.deleted && e.path.startsWith(`${folder.path}/`)) ids.push(id);
    }
    const msg = ids.length
      ? `Delete folder "${folder.name}" and ${ids.length} file${ids.length === 1 ? '' : 's'} inside it?`
      : `Delete folder "${folder.name}"?`;
    if (!confirm(msg)) return;
    for (const id of ids) vault.deleteFile(id);
    setEphemeral(
      (prev) =>
        new Set([...prev].filter((p) => p !== folder.path && !p.startsWith(`${folder.path}/`))),
    );
  };

  // ------------------------------------------------------------------ move

  const moveDestinations = useMemo(() => {
    if (!moving) return [];
    const all = ['', ...allFolderPaths(tree)];
    if (moving.type === 'file') {
      const cur = dirname(moving.path);
      return all.filter((p) => p !== cur);
    }
    const src = moving.path;
    const parent = dirname(src);
    return all.filter((p) => p !== parent && p !== src && !p.startsWith(`${src}/`));
  }, [moving, tree]);

  const doMove = (dest: string) => {
    const m = moving;
    setMoving(null);
    if (!m) return;
    if (m.type === 'file') {
      const newPath = joinPath(dest, basename(m.path));
      const clash = vault.findByPath(newPath);
      if (clash && clash !== m.id) {
        alert(`"${newPath}" already exists.`);
        return;
      }
      vault.renameFile(m.id, newPath);
    } else {
      moveFolderTo(m.path, joinPath(dest, basename(m.path)));
    }
    if (dest) expandTo(dest);
  };

  // ------------------------------------------------------------------ menu

  const ctx: TreeCtx = {
    expanded,
    activeId,
    renaming,
    toggleFolder,
    openFile,
    openFileMenu: (file, x, y) => setMenu({ type: 'file', file, ...clampMenu(x, y) }),
    openFolderMenu: (folder, x, y) => setMenu({ type: 'folder', folder, ...clampMenu(x, y) }),
    finishRename,
    cancelRename,
  };

  const menuItem = (label: string, run: () => void, danger = false) => (
    <button
      key={label}
      className={`ctx-item${danger ? ' danger' : ''}`}
      onClick={() => {
        setMenu(null);
        run();
      }}
    >
      {label}
    </button>
  );

  const isEmpty = tree.folders.length === 0 && tree.files.length === 0;

  return (
    <div className="pane">
      <div className="pane-header">
        <span className="pane-title">Files</span>
        <button className="pane-btn" title="New note" onClick={() => newNote()}>
          ＋
        </button>
        <button className="pane-btn" title="New folder" onClick={() => newFolder()}>
          📁
        </button>
        <button className="pane-btn" title="New canvas" onClick={newCanvas}>
          🗺
        </button>
        <button className="pane-btn" title="Collapse all" onClick={collapseAll}>
          ⌃
        </button>
      </div>

      {isEmpty ? (
        <div className="pane-empty">
          The vault is empty.
          <br />
          Create your first note with ＋ above.
        </div>
      ) : (
        <div className="tree">
          {tree.folders.map((f) => (
            <FolderNode key={f.path} folder={f} depth={0} ctx={ctx} />
          ))}
          {tree.files.map((f) => (
            <FileNode key={f.id} file={f} depth={0} ctx={ctx} />
          ))}
        </div>
      )}

      {menu && (
        <>
          <div className="ctx-backdrop" onClick={() => setMenu(null)} />
          <div className="ctx-menu" style={{ left: menu.x, top: menu.y }}>
            {menu.type === 'file' ? (
              <>
                {menuItem('Rename', () => setRenaming({ type: 'file', id: menu.file.id }))}
                {menuItem('Move to…', () =>
                  setMoving({ type: 'file', id: menu.file.id, path: menu.file.path }),
                )}
                {menu.file.kind === 'note' &&
                  menuItem('Export to PDF', () => exportNoteToPdf(menu.file.id))}
                {menuItem('Delete', () => deleteFileRow(menu.file), true)}
              </>
            ) : (
              <>
                {menuItem('New note', () => newNote(menu.folder.path))}
                {menuItem('New folder', () => newFolder(menu.folder.path))}
                {menuItem('Rename', () => setRenaming({ type: 'folder', path: menu.folder.path }))}
                {menuItem('Move to…', () => setMoving({ type: 'folder', path: menu.folder.path }))}
                {menuItem('Delete', () => deleteFolderRow(menu.folder), true)}
              </>
            )}
          </div>
        </>
      )}

      {moving && (
        <div className="dialog-backdrop" onClick={() => setMoving(null)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-title">Move “{basename(moving.path)}” to…</div>
            <div className="dialog-list">
              {moveDestinations.map((p) => (
                <button key={p || '/'} className="ctx-item" onClick={() => doMove(p)}>
                  {p === '' ? '/ (vault root)' : p}
                </button>
              ))}
              {moveDestinations.length === 0 && (
                <div className="pane-empty">No other folders to move into.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- tree rows

function clampMenu(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - 208)),
    y: Math.max(8, Math.min(y, window.innerHeight - 260)),
  };
}

/** Long-press (500ms) opens the row menu on touch devices. */
function useLongPress(cb: (x: number, y: number) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);
  const clear = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  useEffect(() => clear, [clear]);
  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const { clientX, clientY } = t;
      firedRef.current = false;
      clear();
      timer.current = setTimeout(() => {
        firedRef.current = true;
        cb(clientX, clientY);
      }, 500);
    },
    onTouchMove: clear,
    onTouchEnd: clear,
    onTouchCancel: clear,
  };
  /** True (and resets) when a long-press just fired — swallows the tap's click. */
  const consumeFired = () => {
    const f = firedRef.current;
    firedRef.current = false;
    return f;
  };
  return { handlers, consumeFired };
}

function RenameInput({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const cancelled = useRef(false);
  return (
    <input
      className="tree-rename"
      defaultValue={initial}
      autoFocus
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') {
          cancelled.current = true;
          onCancel();
        }
      }}
      onBlur={(e) => {
        if (!cancelled.current) onSubmit(e.currentTarget.value);
      }}
    />
  );
}

function FolderNode({
  folder,
  depth,
  ctx,
}: {
  folder: TreeFolder;
  depth: number;
  ctx: TreeCtx;
}) {
  const lp = useLongPress((x, y) => ctx.openFolderMenu(folder, x, y));
  const open = ctx.expanded.has(folder.path);
  const isRenaming = ctx.renaming?.type === 'folder' && ctx.renaming.path === folder.path;
  return (
    <div>
      <div
        className="tree-row folder"
        style={{ paddingLeft: 8 + depth * 14 }}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isRenaming && !lp.consumeFired()) ctx.toggleFolder(folder.path);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isRenaming) ctx.toggleFolder(folder.path);
        }}
        {...lp.handlers}
      >
        <span className={`tree-chevron${open ? ' open' : ''}`}>▸</span>
        {isRenaming ? (
          <RenameInput initial={folder.name} onSubmit={ctx.finishRename} onCancel={ctx.cancelRename} />
        ) : (
          <span className="tree-name">{folder.name}</span>
        )}
        <button
          className="tree-more"
          title="Folder actions"
          onClick={(e) => {
            e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            ctx.openFolderMenu(folder, r.left, r.bottom + 2);
          }}
        >
          ⋯
        </button>
      </div>
      {open && (
        <div>
          {folder.folders.map((f) => (
            <FolderNode key={f.path} folder={f} depth={depth + 1} ctx={ctx} />
          ))}
          {folder.files.map((f) => (
            <FileNode key={f.id} file={f} depth={depth + 1} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
}

function FileNode({ file, depth, ctx }: { file: TreeFile; depth: number; ctx: TreeCtx }) {
  const lp = useLongPress((x, y) => ctx.openFileMenu(file, x, y));
  const isRenaming = ctx.renaming?.type === 'file' && ctx.renaming.id === file.id;
  // Notes/canvas rename without their extension (it is re-added on submit).
  const renameInitial = file.kind === 'file' ? basename(file.path) : file.name;
  return (
    <div
      className={`tree-row${ctx.activeId === file.id ? ' active' : ''}`}
      style={{ paddingLeft: 8 + depth * 14 }}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (!isRenaming && !lp.consumeFired()) ctx.openFile(file, e.metaKey || e.ctrlKey);
      }}
      onAuxClick={(e) => {
        // Middle-click → open in a new tab (notes/canvas/base only).
        if (e.button === 1 && !isRenaming && file.kind !== 'file') {
          e.preventDefault();
          ctx.openFile(file, true);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isRenaming) ctx.openFile(file, e.metaKey || e.ctrlKey);
      }}
      {...lp.handlers}
    >
      <span className="tree-icon">{FILE_ICON[file.kind]}</span>
      {isRenaming ? (
        <RenameInput initial={renameInitial} onSubmit={ctx.finishRename} onCancel={ctx.cancelRename} />
      ) : (
        <span className="tree-name">{file.name}</span>
      )}
      <button
        className="tree-more"
        title="File actions"
        onClick={(e) => {
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          ctx.openFileMenu(file, r.left, r.bottom + 2);
        }}
      >
        ⋯
      </button>
    </div>
  );
}
